// Data layer for the pending-approval geo-zone drafts: no-go zones (/WebNoGo) and the
// operational-area edit proposal (/WebOpea). Mirrors the normalize/convert style of
// src/components/poi/poi.js, but simpler — these are short-lived drafts owned by a
// separate external approval application, so there is no localStorage offline-sync layer
// here, just thin wrappers that call the API directly.
//
// The API uses DefaultContractResolver → PascalCase property names (Id, Name, Polygon, …).
// Normalize to camelCase so the rest of the client code can use consistent lowercase keys.
// Polygon wire format is an array of [lat, lng] pairs — convert to/from { lat, lng } objects
// (the shape the rest of the map code — GeoCodes, localPois, google.maps.Polygon paths — uses).

/** Wire format [[lat,lng], ...] → local format [{lat,lng}, ...]. Tolerates already-converted input. */
function polygonToPath(polygon) {
  return (polygon || []).map((pt) =>
    Array.isArray(pt) ? { lat: pt[0], lng: pt[1] } : pt
  );
}

/** Local format [{lat,lng}, ...] → wire format [[lat,lng], ...]. */
function pathToPolygon(path) {
  return (path || []).map(({ lat, lng }) => [lat, lng]);
}

function normalizeNoGo(sp) {
  if (!sp) return null;
  return {
    id:        sp.id        ?? sp.Id,
    unitNo:    sp.unitNo    ?? sp.UnitNo,
    name:      sp.name      ?? sp.Name,
    color:     sp.color     ?? sp.Color ?? "#ff0000",
    path:      polygonToPath(sp.polygon ?? sp.Polygon),
    status:    sp.status    ?? sp.Status,
    reason:    sp.reason    ?? sp.Reason,
    createdAt: sp.createdAt ?? sp.CreatedAt,
    updatedAt: sp.updatedAt ?? sp.UpdatedAt,
  };
}

function normalizeOpea(sp) {
  if (!sp) return null;
  return {
    id:        sp.id        ?? sp.Id,
    unitNo:    sp.unitNo    ?? sp.UnitNo,
    name:      sp.name      ?? sp.Name,
    path:      polygonToPath(sp.polygon     ?? sp.Polygon),
    basePath:  polygonToPath(sp.basePolygon ?? sp.BasePolygon),
    status:    sp.status    ?? sp.Status,
    reason:    sp.reason    ?? sp.Reason,
    createdAt: sp.createdAt ?? sp.CreatedAt,
    updatedAt: sp.updatedAt ?? sp.UpdatedAt,
  };
}

// ── /WebNoGo ──────────────────────────────────────────────────────────────────

/** GET /WebNoGo?unitNo=xxx → array of pending no-go drafts for that vehicle. */
export async function fetchPendingNoGo(axios, unitNo) {
  const { data } = await axios.get("/WebNoGo", { params: { unitNo } });
  return (Array.isArray(data) ? data : []).map(normalizeNoGo);
}

/** POST /WebNoGo — creates a pending no-go draft. { unitNo, name, color, path } */
export async function createNoGo(axios, { unitNo, name, color, path }) {
  const { data } = await axios.post("/WebNoGo", {
    UnitNo:  unitNo,
    Name:    name,
    Color:   color || "#ff0000",
    Polygon: pathToPolygon(path),
  });
  return normalizeNoGo(data);
}

/** PUT /WebNoGo/{id} — updates a still-pending no-go draft. */
export async function updateNoGo(axios, id, { unitNo, name, color, path }) {
  const { data } = await axios.put(`/WebNoGo/${id}`, {
    UnitNo:  unitNo,
    Name:    name,
    Color:   color || "#ff0000",
    Polygon: pathToPolygon(path),
  });
  return normalizeNoGo(data);
}

/** DELETE /WebNoGo/{id} */
export async function deleteNoGo(axios, id) {
  await axios.delete(`/WebNoGo/${id}`);
}

// ── /WebOpea ──────────────────────────────────────────────────────────────────

/** GET /WebOpea?unitNo=xxx → the 0-or-1 pending operational-area draft for that vehicle, or null. */
export async function fetchPendingOpea(axios, unitNo) {
  const { data } = await axios.get("/WebOpea", { params: { unitNo } });
  const list = (Array.isArray(data) ? data : []).map(normalizeOpea);
  return list[0] || null;
}

/**
 * POST /WebOpea — creates a pending operational-area draft.
 * { unitNo, name, path, basePath }. Server enforces one pending draft per vehicle and
 * returns 409 Conflict on a second concurrent create — callers should catch that and
 * resume the existing draft via fetchPendingOpea instead of surfacing an error.
 */
export async function createOpea(axios, { unitNo, name, path, basePath }) {
  const { data } = await axios.post("/WebOpea", {
    UnitNo:      unitNo,
    Name:        name,
    Polygon:     pathToPolygon(path),
    BasePolygon: pathToPolygon(basePath),
  });
  return normalizeOpea(data);
}

/** PUT /WebOpea/{id} — updates the draft's polygon/name. BasePolygon is fixed server-side. */
export async function updateOpea(axios, id, { unitNo, name, path }) {
  const { data } = await axios.put(`/WebOpea/${id}`, {
    UnitNo:  unitNo,
    Name:    name,
    Polygon: pathToPolygon(path),
  });
  return normalizeOpea(data);
}

/** DELETE /WebOpea/{id} — deletes/cancels the draft. */
export async function deleteOpea(axios, id) {
  await axios.delete(`/WebOpea/${id}`);
}
